export type AuditLogCategory = 
  | 'AUTH'
  | 'USER'
  | 'ROLE'
  | 'SUPPLY'
  | 'VEHICLE'
  | 'PERSONNEL'
  | 'SHIPMENT'
  | 'BID'
  | 'OFFER'
  | 'EXPENSE'
  | 'DOCUMENT'
  | 'WAREHOUSE'
  | 'CUSTOMER'
  | 'CAPACITY'
  | 'SETTINGS'
  | 'SECURITY'
  | 'SYSTEM';

export type AuditLogAction = 
  | 'CREATE'
  | 'READ'
  | 'UPDATE'
  | 'DELETE'
  | 'LOGIN'
  | 'LOGOUT'
  | 'APPROVE'
  | 'REJECT'
  | 'SUBMIT'
  | 'CANCEL'
  | 'ASSIGN'
  | 'UNASSIGN'
  | 'UPLOAD'
  | 'DOWNLOAD'
  | 'EXPORT'
  | 'IMPORT'
  | 'STATUS_CHANGE'
  | 'PERMISSION_CHANGE'
  | 'AUTH_REJECTED'
  | 'PERMISSION_DENIED'
  | 'RATE_LIMITED'
  | 'ENDPOINT_PROBE';

export type AuditLogSeverity = 'info' | 'warning' | 'error' | 'critical';

export interface AuditLog {
  id: string;
  timestamp: string;
  category: AuditLogCategory;
  action: AuditLogAction;
  severity: AuditLogSeverity;
  
  // Kullanıcı bilgileri
  userId: string;
  userName: string;
  userRole: string;
  
  // İşlem detayları
  entity: string; // Örn: "Supply", "Vehicle", "User"
  entityId?: string; // Etkilenen kaydın ID'si
  entityName?: string; // Etkilenen kaydın adı
  
  // Değişiklik detayları
  description: string; // İnsan okunabilir açıklama
  changes?: {
    field: string;
    oldValue: any;
    newValue: any;
  }[];
  
  // Ek bilgiler
  metadata?: Record<string, any>; // Ekstra bilgiler
  ipAddress?: string;
  userAgent?: string;
  
  // Sonuç
  success: boolean;
  errorMessage?: string;
}

export interface AuditLogFilter {
  category?: AuditLogCategory[];
  action?: AuditLogAction[];
  severity?: AuditLogSeverity[];
  userId?: string;
  entityId?: string;
  startDate?: string;
  endDate?: string;
  searchQuery?: string;
}

// Kategori bilgileri
export const AUDIT_CATEGORY_INFO: Record<AuditLogCategory, { label: string; color: string; icon: string }> = {
  AUTH: { label: 'Kimlik Doğrulama', color: 'bg-blue-100 text-blue-800', icon: '🔐' },
  USER: { label: 'Kullanıcı', color: 'bg-purple-100 text-purple-800', icon: '👤' },
  ROLE: { label: 'Rol & Yetki', color: 'bg-indigo-100 text-indigo-800', icon: '🛡️' },
  SUPPLY: { label: 'Tedarik', color: 'bg-green-100 text-green-800', icon: '📦' },
  VEHICLE: { label: 'Araç', color: 'bg-orange-100 text-orange-800', icon: '🚛' },
  PERSONNEL: { label: 'Personel', color: 'bg-pink-100 text-pink-800', icon: '👥' },
  SHIPMENT: { label: 'Sevkiyat', color: 'bg-cyan-100 text-cyan-800', icon: '🚚' },
  BID: { label: 'Teklif', color: 'bg-yellow-100 text-yellow-800', icon: '💰' },
  OFFER: { label: 'Teklif Talebi', color: 'bg-amber-100 text-amber-800', icon: '📋' },
  EXPENSE: { label: 'Masraf', color: 'bg-red-100 text-red-800', icon: '💸' },
  DOCUMENT: { label: 'Belge', color: 'bg-gray-100 text-gray-800', icon: '📄' },
  WAREHOUSE: { label: 'Depo', color: 'bg-lime-100 text-lime-800', icon: '🏭' },
  CUSTOMER: { label: 'Müşteri', color: 'bg-teal-100 text-teal-800', icon: '🏢' },
  CAPACITY: { label: 'Kapasite', color: 'bg-emerald-100 text-emerald-800', icon: '📊' },
  SETTINGS: { label: 'Ayarlar', color: 'bg-slate-100 text-slate-800', icon: '⚙️' },
  SECURITY: { label: 'Güvenlik', color: 'bg-red-100 text-red-800', icon: '🛡️' },
  SYSTEM: { label: 'Sistem', color: 'bg-zinc-100 text-zinc-800', icon: '🖥️' },
};

// Aksiyon bilgileri
export const AUDIT_ACTION_INFO: Record<AuditLogAction, { label: string; color: string }> = {
  CREATE: { label: 'Oluşturma', color: 'text-green-600' },
  READ: { label: 'Görüntüleme', color: 'text-blue-600' },
  UPDATE: { label: 'Güncelleme', color: 'text-yellow-600' },
  DELETE: { label: 'Silme', color: 'text-red-600' },
  LOGIN: { label: 'Giriş', color: 'text-green-600' },
  LOGOUT: { label: 'Çıkış', color: 'text-gray-600' },
  APPROVE: { label: 'Onaylama', color: 'text-green-600' },
  REJECT: { label: 'Reddetme', color: 'text-red-600' },
  SUBMIT: { label: 'Gönderme', color: 'text-blue-600' },
  CANCEL: { label: 'İptal', color: 'text-orange-600' },
  ASSIGN: { label: 'Atama', color: 'text-purple-600' },
  UNASSIGN: { label: 'Atamayı Kaldırma', color: 'text-gray-600' },
  UPLOAD: { label: 'Yükleme', color: 'text-blue-600' },
  DOWNLOAD: { label: 'İndirme', color: 'text-cyan-600' },
  EXPORT: { label: 'Dışa Aktarma', color: 'text-indigo-600' },
  IMPORT: { label: 'İçe Aktarma', color: 'text-purple-600' },
  STATUS_CHANGE: { label: 'Durum Değişikliği', color: 'text-yellow-600' },
  PERMISSION_CHANGE: { label: 'Yetki Değişikliği', color: 'text-orange-600' },
  AUTH_REJECTED: { label: 'Kimlik Reddedildi', color: 'text-red-600' },
  PERMISSION_DENIED: { label: 'Yetki Reddedildi', color: 'text-red-600' },
  RATE_LIMITED: { label: 'Limit Aşıldı', color: 'text-orange-600' },
  ENDPOINT_PROBE: { label: 'Endpoint Yoklaması', color: 'text-red-600' },
};

// Severity bilgileri
export const AUDIT_SEVERITY_INFO: Record<AuditLogSeverity, { label: string; color: string; icon: string }> = {
  info: { label: 'Bilgi', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: 'ℹ️' },
  warning: { label: 'Uyarı', color: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: '⚠️' },
  error: { label: 'Hata', color: 'bg-red-50 text-red-700 border-red-200', icon: '❌' },
  critical: { label: 'Kritik', color: 'bg-purple-50 text-purple-700 border-purple-200', icon: '🚨' },
};

// Re-export audit logger fonksiyonları
export { 
  getFilteredAuditLogs, 
  getAuditStats, 
  exportAuditLogs,
  clearAuditLogs,
} from '../utils/auditLogger';
